import type {
  SliderProps
} from '@chakra-ui/react'
import {
  Badge,
  Box,
  forwardRef,
  Grid,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Spinner,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react'
import type { FieldValue, FieldValues } from 'BrokerTsLib/src/rhf'
import React from 'react'

interface CustomSliderProps
  extends Omit<SliderProps, 'min' | 'max' | 'onChange' | 'onChangeEnd'> {
  // use custom props for CustomSlider
  values?: string[]
  labels?: {
    label: string | React.ReactNode
    value: number
  }[]
  enableTooltip?: boolean
  fixedBadge?: {
    isLoading: boolean
    value: string
  }
  // replace omitted props from SliderProps
  min?: string | number | undefined
  max?: string | number | undefined
  onChange?: (value: number, name: FieldValue<FieldValues>) => void
  onChangeEnd?: (value: number, name: FieldValue<FieldValues>) => void
}

export const CustomSlider = forwardRef((props: CustomSliderProps, ref) => {
  const {
    // custom props
    values,
    labels,
    enableTooltip = false,
    fixedBadge,
    // props from SliderProps
    value = undefined,
    defaultValue,
    min,
    max,
    step = 1,
    name,
    isDisabled,
    onChange,
    onChangeEnd,
    ...rest
  } = props
  let _min = min || undefined
  let _max = max || undefined
  const [showTooltip, setShowTooltip] = useBoolean(false)

  if (typeof min === 'undefined') {
    _min = 0
  }

  if (typeof max === 'undefined' && values && values.length > 0) {
    _max = values && values.length - 1
  }

  const handleChange = (_value: number) => {
    onChange?.(_value, name)
  }

  const handleChangeEnd = (_value: number) => {
    onChangeEnd?.(_value, name)
  }

  return (
    <Slider
      min={_min as number}
      max={_max as number}
      step={step}
      value={value}
      sx={{ display: 'grid', alignContent: 'start', h: 4, mb: 6 }}
      ref={ref}
      isDisabled={isDisabled}
      onChange={handleChange}
      onChangeEnd={handleChangeEnd}
      onMouseEnter={setShowTooltip.on}
      onMouseLeave={setShowTooltip.off}
      {...rest}
    >
      <Box sx={{ position: 'static', h: 4 }}>
        {labels &&
          labels.map((label, i: number) => (
            <SliderMark
              key={label.value}
              value={label.value as number}
              sx={{
                fontSize: 'xs',
                top: 7,
                left:
                  i === labels.length - 1
                    ? 'auto !important'
                    : 'auto !important',
                right: i === labels.length - 1 ? '0' : 'auto',
              }}
            >
              {label.label}
            </SliderMark>
          ))}
      </Box>

      <SliderTrack>
        {value !== undefined && !isDisabled && <SliderFilledTrack />}
      </SliderTrack>

      <Tooltip
        bg="neutral.500"
        color="white"
        placement="bottom"
        isOpen={enableTooltip && showTooltip}
        label={`${value}`}
      >
        <SliderThumb />
      </Tooltip>

      {fixedBadge && (
        <Badge
          variant="custom"
          colorScheme="primary"
          sx={{
            display: 'grid',
            position: 'absolute',
            left: '50%',
            top: '22px',
            pointerEvents: 'none',
            placeContent: 'center',
            placeItems: 'center',
            transform: 'translateX(-50%)',
          }}
        >
          {fixedBadge.isLoading ? (
            <Grid sx={{ py: 1 }}>
              <Spinner size="xs" />
            </Grid>
          ) : (
            fixedBadge.value
          )}
        </Badge>
      )}
    </Slider>
  )
})
